import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { forgottonPasswordRequest } from "../api/password.api";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";

type Inputs = {
  email: string;
};

const MainLayoutContainer = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--sumi-sidenav-background);

  a {
    color: var(--text-color);
    text-decoration: underline;
    cursor: pointer;
  }

  h1 {
    margin-top: 2rem;
    font-size: 26px;
    color: var(--sumi-text-primary);
  }

  & label {
    font-size: 12px;
  }
`;

const Form = styled.div`
  width: 90%;
  max-width: 450px;
  padding: 3rem;
  background-color: var(--blue-200);
  color: var(--text-color);
  border-radius: 5px;
`;

const InputContainer = styled.div`
  width: 100%;
  height: 105px;
  text-align: left;

  & .p-inputtext {
    height: 56px;
  }

  & div,
  .p-float-label,
  .p-inputtext,
  .p-password,
  .p-button {
    width: 100%;
  }
`;

const GappedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: justify;
`;

export const RestorePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
  });

  const [toast, theme] = useUIStore(
    useShallow((state: UIState) => [state.toastRef, state.theme])
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: formData,
  });

  const handleRestore = async (data: Inputs) => {
    try {
      // login here
      setLoading(true);

      const result = await forgottonPasswordRequest(data.email);

      if (result) {
        toast?.current?.show({
          severity: "success",
          summary: t("info-success"),
          detail: t("info-restore-password-email-sent"),
          life: 5000,
        });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const getFormErrorMessage = (name: string) => {
    return errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : (
      <></>
    );
  };

  return (
    <MainLayoutContainer className="login-screen">
      <img
        src={theme === "dark" ? "/sumion-white.svg" : "/sumion-blue.svg"}
        alt="Sumi Logo"
        style={{ marginBottom: "1rem", width: "156px" }}
      />
      <h1>{t("header-restore-password")}</h1>
      <Form>
        <form onSubmit={handleSubmit(handleRestore)}>
          <GappedContainer>
            <div>{t("info-restore-password")}</div>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t("warning-required"),
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: t("warning-invalid-email"),
                },
              }}
              render={({ field, fieldState }) => (
                <InputContainer>
                  <label htmlFor={field.name}>{t("label-email")}</label>
                  <InputText
                    id={field.name}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setFormData({ ...formData, email: e.target.value });
                    }}
                    className={classNames({ "p-invalid": fieldState.error })}
                    placeholder={t("label-email")}
                  />
                  {getFormErrorMessage(field.name)}
                </InputContainer>
              )}
            />
            <InputContainer style={{ height: "75px" }}>
              <Button
                id="login-button"
                type="submit"
                label={t("cmd-send")}
                loading={loading}
                style={{ height: "50px" }}
              />
            </InputContainer>

            <InputContainer style={{ height: "25px", textAlign: "center" }}>
              <a
                role="button"
                tabIndex={0}
                id="forgot-password-link"
                onClick={() => navigate("/login")}
                style={{ color: "black" }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    navigate("/login");
                  }
                }}
              >
                {t("label-return-to-login")}
              </a>
            </InputContainer>
          </GappedContainer>
        </form>
      </Form>
    </MainLayoutContainer>
  );
};
